import { ReactNode } from "react";
import { Check, Package, Search } from "lucide-react";
import type { ErpProductSearchItem } from "../../api";

type ErpProductSearchPanelProps = {
  keyword: string;
  results: ErpProductSearchItem[];
  onKeywordChange: (value: string) => void;
  onSearch: () => void;
  onSelect: (item: ErpProductSearchItem) => void;
  title?: string;
  description?: string;
  placeholder?: string;
  searchLabel?: string;
  selectLabel?: string;
  selectedLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  secondaryAction?: ReactNode;
  selectedPredicate?: (item: ErpProductSearchItem) => boolean;
};

function currency(value: number) {
  return new Intl.NumberFormat("zh-TW", {
    currency: "TWD",
    maximumFractionDigits: 0,
    style: "currency",
  }).format(Number(value || 0));
}

function productKey(item: ErpProductSearchItem) {
  return `${item.ProductId}-${item.VariantId || "single"}`;
}

export default function ErpProductSearchPanel({
  keyword,
  results,
  onKeywordChange,
  onSearch,
  onSelect,
  title = "商品搜尋",
  description = "可輸入品名、商品編號或 SKU 查詢；選取後會帶入明細列表。",
  placeholder = "輸入品名、商品編號或 SKU",
  searchLabel = "搜尋商品",
  selectLabel = "選擇",
  selectedLabel = "已選",
  disabled = false,
  loading = false,
  error,
  secondaryAction,
  selectedPredicate,
}: ErpProductSearchPanelProps) {
  return (
    <section className="min-w-0 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="break-words text-sm font-extrabold text-[#203d32]">{title}</h3>
          {description ? <p className="mt-1 break-words text-xs font-bold leading-5 text-[#7b877f]">{description}</p> : null}
        </div>
        {results.length ? <span className="shrink-0 rounded-full bg-[#e9f5ed] px-3 py-1 text-xs font-extrabold text-[#315746]">{results.length} 筆</span> : null}
      </div>

      <div className={`mt-3 grid min-w-0 gap-2 ${secondaryAction ? "lg:grid-cols-[minmax(0,1fr)_150px_140px]" : "lg:grid-cols-[minmax(0,1fr)_150px]"}`}>
        <label className="min-w-0">
          <span className="sr-only">{title}</span>
          <input
            className={`h-11 w-full min-w-0 rounded-md border px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15 ${error ? "border-[#d95f55] bg-[#fff8f7]" : "border-[#cfd8d2] bg-white"}`}
            disabled={disabled}
            onChange={(event) => onKeywordChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onSearch();
              }
            }}
            placeholder={placeholder}
            value={keyword}
          />
        </label>
        <button
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
          disabled={disabled || loading}
          onClick={onSearch}
          type="button"
        >
          <Search size={18} />
          {searchLabel}
        </button>
        {secondaryAction}
      </div>
      {error ? <p className="mt-2 text-sm font-bold text-[#a33a2f]">{error}</p> : null}

      {results.length > 0 ? (
        <div className="mt-3 grid max-h-[360px] gap-2 overflow-y-auto rounded-md border border-[#dfe6e1] bg-white p-2">
          {results.map((item) => {
            const selected = Boolean(selectedPredicate?.(item));
            return (
              <button
                className={`grid min-w-0 gap-3 rounded-md border p-3 text-left transition hover:border-[#85c69b] hover:bg-[#f1faf4] xl:grid-cols-[minmax(220px,1.4fr)_minmax(150px,.75fr)_minmax(180px,.9fr)_minmax(120px,auto)] xl:items-center ${selected ? "border-[#85c69b] bg-[#f1faf4]" : "border-[#edf0ee] bg-[#fbfcfb]"}`}
                key={productKey(item)}
                onClick={() => onSelect(item)}
                type="button"
              >
                <span className="min-w-0">
                  <span className="flex items-start gap-2">
                    <Package className="mt-0.5 shrink-0 text-[#315746]" size={17} />
                    <span className="min-w-0">
                      <span className="block break-words text-base font-extrabold text-[#203d32]">{item.ProductName}</span>
                      <span className="mt-1 block break-words text-xs font-bold text-[#7b877f]">{item.VariantLabel || "單一規格"}</span>
                    </span>
                  </span>
                </span>
                <span className="grid gap-1 text-sm font-bold text-[#59665f]">
                  <span className="break-all">編號：{item.ProductCode || "-"}</span>
                  <span className="break-all">SKU：{item.Sku || "-"}</span>
                </span>
                <span className="grid gap-1 text-sm font-bold text-[#59665f]">
                  <span>售價：<strong className="text-[#203d32]">{currency(item.SalePrice)}</strong></span>
                  <span>ERP 可用：<strong className="text-[#203d32]">{Number(item.AvailableStock || 0).toLocaleString("zh-TW")} 筆</strong></span>
                  <span className={item.SupplierPrice ? "text-[#315746]" : "text-[#a26b00]"}>
                    {item.SupplierPrice ? `供應商成本 ${currency(item.SupplierPrice.UnitCost)}` : "未建供應商價格"}
                  </span>
                  {item.SupplierPrice ? (
                    <span className="break-words text-xs text-[#7b877f]">
                      料號 {item.SupplierPrice.SupplierSku || "-"} / {item.SupplierPrice.PurchaseUnit || "-"} / MOQ {item.SupplierPrice.MinimumOrderQuantity || 0} / 交期 {item.SupplierPrice.LeadTimeDays || 0} 天
                    </span>
                  ) : null}
                </span>
                <span className={`inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-md px-3 text-sm font-extrabold xl:w-auto ${selected ? "border border-[#9ccbb0] bg-white text-[#315746]" : "bg-[#315746] text-white"}`}>
                  {selected ? <Check size={16} /> : null}
                  {selected ? selectedLabel : selectLabel}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
